import type { RouteLocationRaw, RouteRecordNameGeneric } from "vue-router";
import { SQDCBoardsForHierarchyDocument, type SQDCBoardsForHierarchyQuery } from "@/graphql";
import store from "@/store";
import apollo from "@/graphql/apollo";
import { hasRight } from "@/functions/authorizationState";

function setStore(boardId: number, siteId: number) {
  store.commit("login/updateSite", siteId);
  store.commit("login/updateBoard", boardId);
}

function checkBoardAccess(
  routeName: RouteRecordNameGeneric,
  orginalBoardId: number | undefined,
  selectedBoard?: SQDCBoardsForHierarchyQuery["SQDCBoards"][number],
  userId?: number,
  navigate = true,
  requiredSiteRight?: string,
): { boardId: number; redirect?: RouteLocationRaw } | undefined {
  if (
    selectedBoard != null &&
    (requiredSiteRight == null || hasRight(requiredSiteRight, selectedBoard.siteId)) &&
    (selectedBoard.allowedUsersConnection.length === 0 ||
      hasRight("systemSettings.admin") ||
      selectedBoard.allowedUsersConnection.some((c) => c.userId === userId))
  ) {
    setStore(selectedBoard.id, selectedBoard.siteId);
    if (navigate) {
      if (orginalBoardId !== selectedBoard.id || isNaN(selectedBoard.id)) {
        return {
          boardId: selectedBoard.id,
          redirect: {
            name: routeName,
            params: {
              boardId: selectedBoard.id,
            },
          },
        };
      }
    }
    return { boardId: selectedBoard.id };
  }
  return undefined;
}

export const checkId = async (
  routeName: RouteRecordNameGeneric,
  boardId?: number,
  navigate = true,
  requiredSiteRight?: string,
): Promise<RouteLocationRaw | undefined> => {
  const result = await apollo.apolloClient.query<SQDCBoardsForHierarchyQuery>({
    query: SQDCBoardsForHierarchyDocument,
    fetchPolicy: "cache-first",
  });
  let boards = result.data.SQDCBoards;
  const userId = store.getters["login/userId"];
  const preferredSite = store.getters["login/preferredSite"];
  const preferredBoardId = store.getters["login/preferredBoard"];

  if (boardId != null && !isNaN(boardId)) {
    const selectedBoard = boards.find((board) => board.id === boardId);
    const checkBoard = checkBoardAccess(
      routeName,
      boardId,
      selectedBoard,
      userId,
      navigate,
      requiredSiteRight,
    );
    if (checkBoard) {
      // Case selected board is allowed
      return checkBoard.redirect;
    }
  }
  const preferredBoard = boards.find((board) => board.id === preferredBoardId);
  if (preferredBoard?.siteId === preferredSite) {
    const checkBoard = checkBoardAccess(
      routeName,
      boardId,
      preferredBoard,
      userId,
      navigate,
      requiredSiteRight,
    );
    if (checkBoard) {
      // Case preferred board is allowed
      return checkBoard.redirect;
    }
  }

  boards = boards
    .map((board) => ({ ...board, hierarchyLevel: board.path.split(".").length }))
    .toSorted(
      (a, b) =>
        a.siteId - b.siteId || a.hierarchyLevel - b.hierarchyLevel || a.name.localeCompare(b.name),
    );

  for (const board of boards) {
    if (board.siteId === preferredSite) {
      const boardAccess = checkBoardAccess(
        routeName,
        boardId,
        board,
        userId,
        navigate,
        requiredSiteRight,
      );
      if (boardAccess) {
        // Case preferred site is allowed
        return boardAccess.redirect;
      }
    }
  }
  for (const board of boards) {
    const boardAccess = checkBoardAccess(
      routeName,
      boardId,
      board,
      userId,
      navigate,
      requiredSiteRight,
    );
    if (boardAccess) {
      // Case any board is allowed
      return boardAccess.redirect;
    }
  }
  // Case no board is allowed
  return { name: "pageNotFound" };
};
